import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';

const EquipmentPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Sample equipment data - in real app this would come from API
  const equipmentData = [
    {
      id: 1,
      name: "Professional Multimeter",
      category: "testing",
      price: "$149.99",
      image: "/api/placeholder/300/200",
      description: "High-precision digital multimeter for electrical testing",
      specifications: ["True RMS", "CAT III 1000V", "Data logging capability"]
    },
    {
      id: 2,
      name: "Wire Stripper Set",
      category: "tools",
      price: "$89.99",
      image: "/api/placeholder/300/200",
      description: "Professional grade wire stripping tools",
      specifications: ["10-22 AWG range", "Ergonomic handles", "Precision cutting"]
    },
    {
      id: 3,
      name: "Circuit Breaker Panel",
      category: "panels",
      price: "$299.99",
      image: "/api/placeholder/300/200",
      description: "200A main breaker panel with 40 spaces",
      specifications: ["200A capacity", "40 circuits", "UL listed"]
    },
    {
      id: 4,
      name: "LED Work Light",
      category: "lighting",
      price: "$79.99",
      image: "/api/placeholder/300/200",
      description: "Portable LED work light with adjustable stand",
      specifications: ["5000 lumens", "IP65 rated", "Adjustable stand"]
    },
    {
      id: 5,
      name: "Electrical Conduit",
      category: "conduit",
      price: "$24.99",
      image: "/api/placeholder/300/200",
      description: "10ft EMT electrical conduit",
      specifications: ["1/2 inch diameter", "10ft length", "Galvanized steel"]
    },
    {
      id: 6,
      name: "Voltage Tester",
      category: "testing",
      price: "$39.99",
      image: "/api/placeholder/300/200",
      description: "Non-contact voltage tester with LED indicators",
      specifications: ["12-1000V AC", "Non-contact", "Audio/visual alerts"]
    }
  ];

  const categories = [
    { id: 'all', name: 'All Equipment' },
    { id: 'testing', name: 'Testing Equipment' },
    { id: 'tools', name: 'Hand Tools' },
    { id: 'panels', name: 'Electrical Panels' },
    { id: 'lighting', name: 'Lighting' },
    { id: 'conduit', name: 'Conduit & Fittings' }
  ];

  const filteredEquipment = equipmentData.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-emerald-900">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header with Back Button */}
        <div className="mb-8">
          <Link 
            to="/"
            className="inline-flex items-center text-white hover:text-purple-300 transition-colors duration-200 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Electrical Equipment
            </h1>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              Professional-grade electrical equipment and tools for contractors and technicians.
            </p>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="mb-8 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Search Bar */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">Search Equipment</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or description..."
                  className="w-full px-4 py-3 pl-10 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/30"
                />
                <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/30"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id} className="bg-gray-800">
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-300">
            Showing {filteredEquipment.length} of {equipmentData.length} products
          </p>
        </div>

        {/* Equipment Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {filteredEquipment.map(item => (
            <div key={item.id} className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
              
              {/* Equipment Image */}
              <div className="mb-4 bg-white/20 rounded-lg p-4 h-48 flex items-center justify-center">
                <svg className="w-16 h-16 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>

              {/* Equipment Details */}
              <div className="space-y-3">
                <h3 className="text-xl font-semibold text-white">{item.name}</h3>
                <p className="text-gray-300 text-sm">{item.description}</p>
                
                {/* Specifications */}
                <div>
                  <h4 className="text-sm font-medium text-purple-300 mb-2">Key Features:</h4>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {item.specifications.map((spec, index) => (
                      <li key={index} className="flex items-center">
                        <svg className="w-4 h-4 text-emerald-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {spec}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Price and Action */}
                <div className="flex items-center justify-between pt-4 border-t border-white/20">
                  <span className="text-2xl font-bold text-white">{item.price}</span>
                  <div className="space-x-2">
                    <button className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-indigo-700 transition-all duration-200">
                      Quote
                    </button>
                    <button className="px-4 py-2 bg-white/20 text-white rounded-lg text-sm font-medium hover:bg-white/30 transition-all duration-200">
                      Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 text-center">
          <h2 className="text-2xl font-semibold text-white mb-4">
            Need Custom Equipment or Bulk Orders?
          </h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            We offer custom equipment sourcing and bulk pricing for large projects. Contact our equipment specialists for personalized assistance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition-all duration-200"
            >
              Request Quote
            </Link>
            <button className="px-6 py-3 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-all duration-200">
              Call (555) 123-4567
            </button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default EquipmentPage;